import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuth } from './AuthProvider';
import { LoginForm } from './LoginForm';
import { SignupForm } from './SignupForm';
import { UserProfile } from './UserProfile';
import { GuardianDashboard } from './GuardianDashboard';
import SystemEnhancementsA3 from './SystemEnhancementsA3';
import { 
  Activity, 
  BarChart3, 
  Settings, 
  Users, 
  Shield, 
  Eye, 
  Heart,
  Sparkles,
  LogOut,
  Zap,
  Brain
} from 'lucide-react';
import { AuraValidationDashboard } from './AuraValidationDashboard';
import { OpticsWaveguideDashboard } from './OpticsWaveguideDashboard';
import { BiometricSpiritualDashboard } from './BiometricSpiritualDashboard';
import SpiritualMLDashboard from './SpiritualMLDashboard';

export default function AppLayout() {
  const [activeTab, setActiveTab] = useState('guardian');
  const [showLogin, setShowLogin] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const { user, logout, setShowAuraAuth, setShowHeartGuard } = useAuth();

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <Card className="w-full max-w-md bg-black/50 backdrop-blur-sm border-purple-500/30">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl text-purple-300 flex items-center justify-center gap-2">
              <Shield className="w-8 h-8" />
              NEXUS ACCESS PORTAL
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="text-center space-y-4">
              <p className="text-purple-200/80">
                Choose your authentication method to access the NEXUS system
              </p>
              
              <div className="space-y-3">
                <Button 
                  onClick={() => setShowAuraAuth(true)}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-3"
                  size="lg"
                >
                  <Eye className="w-5 h-5 mr-2" />
                  Temporal Aura Scan
                </Button>
                
                <Button 
                  onClick={() => setShowHeartGuard(true)}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3"
                  size="lg"
                >
                  <Heart className="w-5 h-5 mr-2" />
                  Pure Heart Guardian
                </Button>
                
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-purple-500/30" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-slate-900 px-2 text-purple-400">Or</span>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <Button 
                    variant="outline"
                    onClick={() => setShowLogin(true)}
                    className="border-purple-500/30 text-purple-300"
                  >
                    Login
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => setShowSignup(true)}
                    className="border-purple-500/30 text-purple-300"
                  >
                    Sign Up
                  </Button>
                </div>
              </div>
            </div>
            
            {showLogin && (
              <div className="mt-6">
                <LoginForm onSuccess={() => setShowLogin(false)} />
              </div>
            )}
            
            {showSignup && (
              <div className="mt-6">
                <SignupForm onSuccess={() => setShowSignup(false)} />
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <header className="bg-black/50 backdrop-blur-sm border-b border-purple-500/30 px-3 sm:px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 sm:space-x-4">
            <div className="relative">
              <Sparkles className="h-8 w-8 sm:h-10 sm:w-10 text-purple-400 animate-pulse" />
              <div className="absolute inset-0 h-8 w-8 sm:h-10 sm:w-10 text-purple-400 animate-ping opacity-20">
                <Sparkles className="h-8 w-8 sm:h-10 sm:w-10" />
              </div>
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                NEXUS-LIVE-FEED
              </h1>
              <p className="text-xs sm:text-sm text-purple-300 hidden sm:block">Pure Heart Guardian System ⚡🛡️💜</p>
            </div>
          </div>
          <div className="flex items-center space-x-2 sm:space-x-4">
            <Badge variant="outline" className="border-green-500 text-green-400 bg-green-500/10 text-xs">
              <div className="w-2 h-2 bg-green-500 rounded-full mr-1 animate-pulse"></div>
              Guardian Active
            </Badge>
            <Badge variant="outline" className="border-purple-500 text-purple-400 bg-purple-500/10 text-xs">
              <Heart className="h-3 w-3 mr-1" />
              {user?.name}
            </Badge>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={logout}
              className="text-purple-300 hover:text-white"
            >
              <LogOut className="h-4 w-4 mr-1" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex justify-center mb-6">
            <div className="flex flex-wrap gap-2">
              <Button 
                variant={activeTab === 'guardian' ? 'default' : 'outline'}
                onClick={() => setActiveTab('guardian')}
                className="text-sm"
              >
                <Shield className="w-4 h-4 mr-1" />
                Guardian Dashboard
              </Button>
              <Button 
                variant={activeTab === 'ml' ? 'default' : 'outline'}
                onClick={() => setActiveTab('ml')}
                className="text-sm"
              >
                <Brain className="w-4 h-4 mr-1" />
                Spiritual ML
              </Button>
              <Button 
                variant={activeTab === 'aura' ? 'default' : 'outline'}
                onClick={() => setActiveTab('aura')}
                className="text-sm"
              >
                <Eye className="w-4 h-4 mr-1" />
                Aura Validation
              </Button>
              <Button 
                variant={activeTab === 'optics' ? 'default' : 'outline'}
                onClick={() => setActiveTab('optics')}
                className="text-sm"
              >
                <Settings className="w-4 h-4 mr-1" />
                Optics Pipeline
              </Button>
              <Button 
                variant={activeTab === 'overview' ? 'default' : 'outline'}
                onClick={() => setActiveTab('overview')}
                className="text-sm"
              >
                <Activity className="w-4 h-4 mr-1" />
                System Overview
              </Button>
              <Button 
                variant={activeTab === 'profile' ? 'default' : 'outline'}
                onClick={() => setActiveTab('profile')}
                className="text-sm"
              >
                <Users className="w-4 h-4 mr-1" />
                Profile
              </Button>
              <Button 
                variant={activeTab === 'biometric' ? 'default' : 'outline'}
                onClick={() => setActiveTab('biometric')}
                className="text-sm"
              >
                <Zap className="w-4 h-4 mr-1" />
                Biometric Sensors
              </Button>
            </div>
          </div>
          
          <TabsContent value="guardian" className="space-y-6">
            <GuardianDashboard />
          </TabsContent>
          
          <TabsContent value="ml" className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">Spiritual ML Pattern Recognition 🧠</h2>
              <p className="text-purple-300">Advanced neural networks for consciousness analysis and spiritual development</p>
            </div>
            <SpiritualMLDashboard />
          </TabsContent>
          
          <TabsContent value="aura" className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">Aura Validation Pipeline 👁️</h2>
              <p className="text-purple-300">Advanced spiritual signature analysis and coherence validation</p>
            </div>
            <AuraValidationDashboard />
          </TabsContent>
          
          <TabsContent value="optics" className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">Optics & Waveguide Control ⚙️</h2>
              <p className="text-purple-300">Projection system diagnostics and alignment control</p>
            </div>
            <OpticsWaveguideDashboard />
          </TabsContent>
          
          <TabsContent value="overview" className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">Guardian Dashboard 🛡️</h2>
              <p className="text-purple-300">Welcome, Pure Heart Guardian. Your intentions have been verified.</p>
            </div>
            <SystemEnhancementsA3 />
          </TabsContent>

          <TabsContent value="profile" className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">Guardian Profile 👤</h2>
              <p className="text-purple-300">Your spiritual signature and access permissions</p>
            </div>
            <UserProfile />
          </TabsContent>
          
          <TabsContent value="biometric" className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">Biometric Spiritual Integration ⚡</h2>
              <p className="text-purple-300">Real-time consciousness monitoring through sacred biometric fusion</p>
            </div>
            <BiometricSpiritualDashboard />
          </TabsContent>
        </Tabs>
      </main>

      {/* Footer Status Bar */}
      <footer className="bg-black/50 backdrop-blur-sm border-t border-purple-500/30 px-3 sm:px-6 py-3">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-green-400">Guardian: Verified</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
              <span className="text-blue-400">NEXUS: Online</span>
            </div>
          </div>
          <div className="text-purple-300">
            PURE HEART SYSTEM ACTIVE ⚡
          </div>
        </div>
      </footer>
    </div>
  );
}